// Shared utilities consumed across multiple Haklai domains.
// Domain-specific logic lives in:
//   - propertyParts/logic/haklaiPropertyPartsHelpers.js
//   - expenses/logic/haklaiExpensesHelpers.js
//   - preCalc/logic/haklaiPreCalcHelpers.js

import {
  CATEGORIES,
  HAKLAI_FARM_REMAINDER_KEY,
  HAKLAI_FARM_REMAINDER_LABEL
} from "./haklaiConstants";

// ── Primitive value predicates ─────────────────────────────────────────────
export const hasPositiveNumericValue = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0;
};

export const hasRawNumericValue = (value) => value !== null && value !== undefined && value !== "";

// ── Property part predicates ───────────────────────────────────────────────
export const isOptionalPartCategory = (category) =>
  category === CATEGORIES.COMMERCIAL
  || category === CATEGORIES.COMMERCIAL_BG
  || category === CATEGORIES.LAND_AND_SHARED;

export const isHaklaiBuiltValueRelevant = (part = {}, hasZaban = false) => (
  part?.category === CATEGORIES.RESIDENTIAL
  && hasZaban === true
);

export const isPropertyPartStarted = (part) => {
  if (!part) return false;
  if (isOptionalPartCategory(part.category) && String(part.label || "").trim()) return true;
  return hasRawNumericValue(part.value) || hasRawNumericValue(part.valueWithoutZaban);
};

// Visual-only sibling of `isPropertyPartStarted`: same shape, but named by
// purpose for call sites that are styling draft rows rather than validating.
export const hasEnteredPropertyPartFieldData = (part) => {
  if (!part) return false;
  if (isOptionalPartCategory(part.category) && String(part.label || "").trim()) return true;
  return hasRawNumericValue(part.value) || hasRawNumericValue(part.valueWithoutZaban);
};

// ── Row ordering ───────────────────────────────────────────────────────────
const getPropertyPartKeySortParts = (key = "") => {
  const match = String(key || "").match(/^(.*)_(\d+)$/);
  if (!match) return { prefix: String(key || ""), index: Number.POSITIVE_INFINITY, raw: String(key || "") };
  return { prefix: match[1], index: Number(match[2]), raw: String(key || "") };
};

export const buildSortedPropertyPartRows = (propertyPartsData = {}, category) =>
  Object.entries(propertyPartsData)
    .filter(([, part]) => part?.category === category)
    .sort(([a], [b]) => {
      const first = getPropertyPartKeySortParts(a);
      const second = getPropertyPartKeySortParts(b);
      const prefixCompare = first.prefix.localeCompare(second.prefix);
      if (prefixCompare !== 0) return prefixCompare;
      if (first.index !== second.index) return first.index - second.index;
      return first.raw.localeCompare(second.raw);
    });

const buildPropertyPartRowsByCategory = (propertyPartsData = {}) => ({
  [CATEGORIES.RESIDENTIAL]: buildSortedPropertyPartRows(propertyPartsData, CATEGORIES.RESIDENTIAL),
  [CATEGORIES.COMMERCIAL]: buildSortedPropertyPartRows(propertyPartsData, CATEGORIES.COMMERCIAL),
  [CATEGORIES.LAND_AND_SHARED]: buildSortedPropertyPartRows(propertyPartsData, CATEGORIES.LAND_AND_SHARED),
  [CATEGORIES.COMMERCIAL_BG]: buildSortedPropertyPartRows(propertyPartsData, CATEGORIES.COMMERCIAL_BG),
  [CATEGORIES.FARM_REMAINDER]: buildSortedPropertyPartRows(propertyPartsData, CATEGORIES.FARM_REMAINDER)
});

const buildOrderedPropertyPartRows = (rowsByCategory = {}) => ([
  ...(rowsByCategory[CATEGORIES.RESIDENTIAL] || []),
  ...(rowsByCategory[CATEGORIES.COMMERCIAL] || []),
  ...(rowsByCategory[CATEGORIES.COMMERCIAL_BG] || []),
  ...(rowsByCategory[CATEGORIES.LAND_AND_SHARED] || []),
  ...(rowsByCategory[CATEGORIES.FARM_REMAINDER] || [])
]);

export const isIgnorableTrailingPropertyPartRow = (rows = [], index) => {
  if (index !== rows.length - 1) return false;
  const [, part] = rows[index] || [];
  if (part?.category === CATEGORIES.RESIDENTIAL) return false;
  return !isPropertyPartStarted(part);
};

// Visual-only mirror of `isIgnorableTrailingPropertyPartRow`. A row qualifies
// as "visually drafting" when it is the last row in its category and no
// user-facing field has data yet (label / sale value / built value).
export const isVisuallyDraftTrailingPropertyPartRow = (rows = [], index) => {
  if (index !== rows.length - 1) return false;
  const [, part] = rows[index] || [];
  return !hasEnteredPropertyPartFieldData(part);
};

export const getRenderablePropertyPartRows = (propertyPartsData = {}) => {
  const rowsByCategory = buildPropertyPartRowsByCategory(propertyPartsData);
  const orderedRows = buildOrderedPropertyPartRows(rowsByCategory);

  return orderedRows.filter(([key, part]) => {
    const categoryRows = rowsByCategory[part?.category] || [];
    const rowIndex = categoryRows.findIndex(([rowKey]) => rowKey === key);
    if (rowIndex === -1) return false;
    return !isIgnorableTrailingPropertyPartRow(categoryRows, rowIndex);
  });
};

const getRenderableResidentialPropertyPartRows = (propertyPartsData = {}) => {
  const residentialRows = buildSortedPropertyPartRows(propertyPartsData, CATEGORIES.RESIDENTIAL);
  return residentialRows.filter((entry, index) => (
    !isIgnorableTrailingPropertyPartRow(residentialRows, index)
  ));
};

const hasIncompleteResidentialRemainderInputs = (residentialRows = [], hasZaban = false) => (
  residentialRows.some(([, part]) => {
    const value = Number(part?.value);
    if (!hasRawNumericValue(part?.value) || !Number.isFinite(value) || value <= 0) return true;

    if (hasZaban !== true) return false;

    const valueWithoutZaban = Number(part?.valueWithoutZaban);
    return (
      !hasRawNumericValue(part?.valueWithoutZaban)
      || !Number.isFinite(valueWithoutZaban)
      || valueWithoutZaban <= 0
      || valueWithoutZaban > value
    );
  })
);

export const getHaklaiFarmRemainderValue = (
  propertyPartsData = {},
  totalPropertyCost = null,
  hasZaban = false
) => {
  const total = Number(totalPropertyCost);
  if (!Number.isFinite(total) || total <= 0) return 0;

  const residentialRows = getRenderableResidentialPropertyPartRows(propertyPartsData);
  if (hasIncompleteResidentialRemainderInputs(residentialRows, hasZaban)) return 0;

  const residentialTotal = residentialRows.reduce((sum, [, part]) => sum + (Number(part?.value) || 0), 0);
  const remainder = total - residentialTotal;
  return Number.isFinite(remainder) && remainder > 0 ? remainder : 0;
};

export const getHaklaiFarmRemainderDisplayState = (
  propertyPartsData = {},
  totalPropertyCost = null,
  hasZaban = false
) => {
  const total = Number(totalPropertyCost);
  if (!Number.isFinite(total) || total <= 0) return { status: "incomplete", value: null };

  const residentialRows = getRenderableResidentialPropertyPartRows(propertyPartsData);
  if (hasIncompleteResidentialRemainderInputs(residentialRows, hasZaban)) {
    return { status: "incomplete", value: null };
  }

  const residentialTotal = residentialRows.reduce((sum, [, part]) => sum + (Number(part?.value) || 0), 0);
  const remainder = total - residentialTotal;
  if (!Number.isFinite(remainder)) return { status: "incomplete", value: null };
  if (remainder < 0) return { status: "invalid", value: null };
  if (remainder === 0) return { status: "zero", value: 0 };
  return { status: "value", value: remainder };
};

export const getHaklaiFarmRemainderPart = (value) => ({
  category: CATEGORIES.FARM_REMAINDER,
  label: HAKLAI_FARM_REMAINDER_LABEL,
  assetTypeKey: "",
  value,
  valueWithoutZaban: value,
  constructionsDoneBeforeApril1997: false
});

export const getHaklaiLogicalPropertyPartsData = (
  propertyPartsData = {},
  totalPropertyCost = null,
  hasZaban = false
) => {
  const hasTotalPropertyCostContext =
    totalPropertyCost !== null
    && totalPropertyCost !== undefined
    && totalPropertyCost !== "";
  if (!hasTotalPropertyCostContext) return propertyPartsData || {};

  const residentialPartsData = Object.entries(propertyPartsData || {}).reduce((acc, [partKey, part]) => {
    if (part?.category === CATEGORIES.RESIDENTIAL) acc[partKey] = part;
    return acc;
  }, {});
  const farmRemainderValue = getHaklaiFarmRemainderValue(residentialPartsData, totalPropertyCost, hasZaban);
  if (!(farmRemainderValue > 0)) {
    return residentialPartsData;
  }
  return {
    ...residentialPartsData,
    [HAKLAI_FARM_REMAINDER_KEY]: getHaklaiFarmRemainderPart(farmRemainderValue)
  };
};

export const getHaklaiLogicalPropertyPartKey = ({
  partKey = "",
  propertyPartsData = {},
  logicalPropertyPartsData = null,
  totalPropertyCost = null,
  hasZaban = false
} = {}) => {
  const logicalParts = logicalPropertyPartsData || getHaklaiLogicalPropertyPartsData(
    propertyPartsData,
    totalPropertyCost,
    hasZaban
  );
  if (!partKey) return "";
  if (logicalParts?.[partKey]) return partKey;

  const sourcePart = propertyPartsData?.[partKey];
  if (
    sourcePart?.category
    && sourcePart.category !== CATEGORIES.RESIDENTIAL
    && logicalParts?.[HAKLAI_FARM_REMAINDER_KEY]
  ) {
    return HAKLAI_FARM_REMAINDER_KEY;
  }
  return "";
};

export const mergeHaklaiPartKeyedDepreciationProfile = (current = null, next = {}) => {
  if (!current || typeof current !== "object" || Array.isArray(current)) return next;
  const currentResult = Number(current.depreciationResult);
  const nextResult = Number(next?.depreciationResult);
  if (!Number.isFinite(currentResult) || !Number.isFinite(nextResult)) return next;
  return {
    ...current,
    ...next,
    depreciationResult: currentResult + nextResult,
    depCurrency: next.depCurrency || current.depCurrency
  };
};

export const HAKLAI_PRE_1997_MINIMAL_ZABAN_THRESHOLD = 303501;

export const isHaklaiPre1997MinimalZabanRelevantForPart = (part = {}, hasZaban = false) => {
  if (hasZaban !== true) return false;
  if (part?.category !== CATEGORIES.RESIDENTIAL) return false;

  const saleValue = Number(part?.value);
  const builtValue = Number(part?.valueWithoutZaban);

  return (
    Number.isFinite(saleValue)
    && saleValue > 0
    && Number.isFinite(builtValue)
    && builtValue > 0
    && builtValue < saleValue
    && builtValue < HAKLAI_PRE_1997_MINIMAL_ZABAN_THRESHOLD
  );
};

// ── Display label (auto-numbered per-category for duplicate types) ─────────
// A part's display string is its Hebrew label, optionally PREFIXED with a
// 1-based ordinal when more than one row in the same category points to the
// same `assetTypeKey`. The number is placed at the start of the source
// string so that — in this RTL context — it renders at the visual right
// edge (the start of the input), keeping it visible even when the type
// label itself is long enough to be truncated.
//
// The numbering re-flows automatically as rows are added, removed or
// retyped — there is no persisted alias.
//
// Residential rows are excluded from numbering: their labels are preassigned
// (בית מגורים ראשי / שני ...) and unique by construction.
//
// `assetTypeKey` is the canonical identity. We fall back to the Hebrew label
// only as a safety net for not-yet-normalized legacy data.
const getPartTypeIdentity = (part) => {
  const assetTypeKey = String(part?.assetTypeKey || "").trim();
  if (assetTypeKey) return assetTypeKey;
  return String(part?.label || "").trim();
};

export const getHaklaiPartDisplayLabel = (entry, siblingsInCategory = []) => {
  const [rowKey, part] = entry || [];
  const baseLabel = String(part?.label || "").trim();
  if (!baseLabel) return "";
  if (!isOptionalPartCategory(part?.category)) return baseLabel;

  const partTypeIdentity = getPartTypeIdentity(part);
  if (!partTypeIdentity) return baseLabel;

  const sameTypeSiblings = siblingsInCategory.filter(([, sibling]) => (
    getPartTypeIdentity(sibling) === partTypeIdentity
  ));
  if (sameTypeSiblings.length <= 1) return baseLabel;

  const ordinal = sameTypeSiblings.findIndex(([key]) => key === rowKey) + 1;
  return ordinal > 0 ? `(${ordinal})${baseLabel}` : baseLabel;
};

export const getHaklaiAssetGroups = (propertyPartsData = {}) => {
  const rowsByCategory = buildPropertyPartRowsByCategory(propertyPartsData);
  return getRenderablePropertyPartRows(propertyPartsData).map(([partKey, part]) => ({
    id: partKey,
    partKey,
    title: getHaklaiPartDisplayLabel([partKey, part], rowsByCategory[part?.category] || [])
  }));
};

// ── Acquisition cost computation ───────────────────────────────────────────
// Distributes totalAcquisitionCost across parts proportionally to their sale value.
// The last part absorbs any rounding remainder.
export const getHaklaiComputedAcquisitionByPart = (propertyPartsData = {}, totalAcquisitionCost) => {
  const renderableRows = getRenderablePropertyPartRows(propertyPartsData);
  const normalizedTotalAcquisitionCost = Number(totalAcquisitionCost);
  const computed = {};

  renderableRows.forEach(([key]) => { computed[key] = 0; });

  if (!hasPositiveNumericValue(normalizedTotalAcquisitionCost)) return computed;

  const totalSaleValue = renderableRows.reduce((sum, [, part]) => sum + (Number(part?.value) || 0), 0);
  if (!(totalSaleValue > 0)) return computed;

  let assignedTotal = 0;
  const lastKey = renderableRows.length ? renderableRows[renderableRows.length - 1][0] : "";

  renderableRows.forEach(([key, part]) => {
    const partSaleValue = Number(part?.value) || 0;
    if (key === lastKey) {
      computed[key] = Math.round(normalizedTotalAcquisitionCost - assignedTotal);
      return;
    }
    const partAcquisitionCost = Math.round((partSaleValue / totalSaleValue) * normalizedTotalAcquisitionCost);
    computed[key] = partAcquisitionCost;
    assignedTotal += partAcquisitionCost;
  });

  return computed;
};

// ── Zaban (building rights) helpers ───────────────────────────────────────
// Zaban attribution is relevant when a residential part's total value differs
// from its built-only value, indicating land value with building rights.
export const isHaklaiZabanAttributionRelevant = (propertyPartsData = {}, targetPartKey, hasZaban) => {
  if (!hasZaban || !targetPartKey) return false;
  const part = propertyPartsData[targetPartKey];
  if (!part || part.category !== CATEGORIES.RESIDENTIAL) return false;

  const partSaleValue = Number(part.value);
  const hasBuiltValueRaw =
    part.valueWithoutZaban !== null && part.valueWithoutZaban !== undefined && part.valueWithoutZaban !== "";
  if (!hasBuiltValueRaw) return false;

  const builtValue = Number(part.valueWithoutZaban);
  if (!Number.isFinite(partSaleValue) || partSaleValue <= 0) return false;
  if (!Number.isFinite(builtValue) || builtValue <= 0) return false;

  return partSaleValue - builtValue > 0;
};

export const getHaklaiZabanAttributionSelections = (value) => {
  if (value === "both") return ["residence", "zaban"];
  if (value === "residence" || value === "zaban") return [value];
  return [];
};

export const buildHaklaiZabanAttributionValue = (selectedValues = []) => {
  const hasResidence = selectedValues.includes("residence");
  const hasZaban = selectedValues.includes("zaban");
  if (hasResidence && hasZaban) return "both";
  if (hasResidence) return "residence";
  if (hasZaban) return "zaban";
  return "";
};
